import * as React from "react";
import { Button, Card } from "antd";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { CardContent } from "../../components/antd/card/CardContent";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { parseLink } from "../../utils/linkParser/linkParser";
import { linksOf } from "../../services/cpln";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameColumn } from "../../newcomponents/table/columns/wellKnown/nameColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableModal } from "../../components/antd/TableModal";
import { Table } from "../../newcomponents/table/table";
import { NameNoLinkColumn } from "../../newcomponents/table/columns/wellKnown/nameNoLinkColumn";
import { OriginColumn } from "../../newcomponents/table/columns/wellKnown/originColumn";
import { ServiceAccount } from "../../schema/types/serviceAccount/entity";
import { GroupInput } from "../../schema/types/group/input";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { cloneDeep } from "lodash";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";
import { ServiceAccountTableItem } from "../../mst/kinds/serviceAccount.helpers";

interface Props {}
export const ServiceAccountItems: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const { kind: addTableKind, id: addTableId } = useTableKindId("serviceaccount", "group-serviceaccount-items");
  const qData = useTableItemQueryData<ServiceAccount>(addTableKind, {});

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<ServiceAccountTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, ServiceAccountTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  function itemFrom(data: string | ServiceAccount): ServiceAccountTableItem {
    let item = data as ServiceAccount;

    if (typeof data === "string") {
      const link = data;
      item = itemCache[link];
      if (!item) {
        // TODO move to a central place and make for each item kind, maybe utils
        return {
          id: "",
          name: parseLink(link, { useInputCtx: true }).name,
          description: "",
          kind: "serviceaccount",
          origin: "default",
          version: 0,
          tags: {},
          created: "",
          lastModified: "",
          links: [{ rel: "self", href: link }],
          selfLink: link,
        };
      }
    }

    const selfLink = linksOf(item).self!;

    return {
      ...item,
      selfLink: selfLink,
    };
  }

  const serviceAccounts = useCpFormValue(`serviceAccounts`);
  const serviceAccountTableItems: ServiceAccountTableItem[] = React.useMemo(
    () => serviceAccounts.map((sa) => itemFrom(sa.link)),
    [serviceAccounts],
  );

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: GroupInput = form.values as any;

    const currentServiceAccounts: KindSelectState[] = cloneDeep(values.serviceAccounts);

    currentServiceAccounts.splice(index, 1);
    formSetValue(id, `serviceAccounts`, currentServiceAccounts);

    formTriggerValidation(id);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: GroupInput = form.values as any;

    const currentServiceAccounts: KindSelectState[] = cloneDeep(values.serviceAccounts);

    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentServiceAccounts.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentServiceAccounts.push({
        value: name,
        item: item,
        link: selectionToAdd,
      });
    }

    // TODO initialize this item cache on load for detail page
    setItemCache(itemCache);

    formSetValue(id, `serviceAccounts`, currentServiceAccounts);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);

    formTriggerValidation(id);
  }

  const currentLinks = serviceAccounts.map((sa) => sa.link);
  const addServiceAccountTableItems: ServiceAccountTableItem[] = qData.visibleItems
    .map((item) => itemFrom(item))
    .filter((item) => !currentLinks.includes(item.selfLink));

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Service Account</CardTitle>
          <CardContent>
            {serviceAccountTableItems.length > 0 ? (
              <Table<ServiceAccountTableItem>
                tableId="group-serviceaccount-items-editable"
                data={serviceAccountTableItems}
                hideSettings
                columns={[
                  NameNoLinkColumn({ enableSort: false }),
                  OriginColumn(),
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Button
                            htmlType={`button`}
                            type={"text"}
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => onRemoveItem(row.index)}
                          />
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            <TableModal
              open={isAddingItem}
              onCancel={() => setIsAddingItem(false)}
              onOk={() => {
                onOkAddModal();
              }}
            >
              <Table<ServiceAccountTableItem>
                tableId={addTableId}
                title={"Select Service Accounts to Add"}
                selectMode="multi"
                selectKey="selfLink"
                selections={selectionsToAdd}
                onSelectionsChange={(keys, items) => {
                  setSelectionsToAdd(keys);
                  setSelectionItems(items!);
                }}
                data={addServiceAccountTableItems}
                headerRenderer={() => {
                  return (
                    <>
                      <TableHeaderRefreshButton onClick={qData.fetchItems} />
                      <CustomTableCPLNQuery
                        tableId={addTableId}
                        kind={addTableKind}
                        onQuery={qData.setQuery}
                        isLoading={qData.isLoadingQueried}
                      />
                    </>
                  );
                }}
                enableFilter
                isLoading={qData.isLoading}
                onSortChange={qData.setSort}
                onSearchChange={qData.setSearch}
                isLoadingMore={qData.isLoadingMore}
                hasMore={qData.hasMore}
                onLoadMore={qData.fetchNextPage}
                columns={[
                  NameColumn(undefined, { shouldForceTargetBlank: () => true }),
                  OriginColumn(),
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                ]}
              />
            </TableModal>
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => setIsAddingItem(true)}
            >
              Add Service Account
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
